import { db, auth, functions } from '../firebaseConfig.js';
import { collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, limit, startAfter, getDocsFromCache, getDocsFromServer, doc, updateDoc, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ensureFreshToken } from './authUtils.js';

// Get nested comments (replies to a specific comment)
export async function getNestedComments(parentCommentId, opts = {}) {
  const { pageSize = 20, cursor = null, fetchPolicy = 'cache-first' } = opts;
  const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
  const commentsRef = collection(db, 'solution_reason_comments');
  let q;
  
  try {
    // Ensure fresh auth token before reads
    await ensureFreshToken();
    
    q = cursor
      ? query(
          commentsRef,
          where('parentCommentId', '==', parentCommentId),
          orderBy('timestamp', 'asc'),
          startAfter(cursor),
          limit(pageSize)
        )
      : query(
          commentsRef,
          where('parentCommentId', '==', parentCommentId),
          orderBy('timestamp', 'asc'),
          limit(pageSize)
        );
    
    // Cache-first: try cache if requested
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          console.debug('[getNestedComments] cache snapshot', {
            parentCommentId,
            fromCache: cachedSnap?.metadata?.fromCache ?? 'unknown',
            size: cachedSnap?.size,
            hasPendingWrites: cachedSnap?.metadata?.hasPendingWrites ?? 'unknown'
          });
          const comments = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { parentCommentId, comments, nextCursor, source: 'cache' };
        }
      } catch (_) {
        // cache miss, proceed to server
      }
      // If low quota, skip server and return empty
      if (lowQuota) {
        return { parentCommentId, comments: [], nextCursor: null, source: 'skipped' };
      }
    }
    
    // network-only forces server; otherwise prefer server to avoid staleness under persistent cache
    let snapshot;
    try {
      if (fetchPolicy === 'network-only' && typeof getDocsFromServer === 'function') {
        snapshot = await getDocsFromServer(q);
      } else {
        snapshot = await (typeof getDocsFromServer === 'function' ? getDocsFromServer(q) : getDocs(q));
      }
    } catch (e) {
      snapshot = await getDocs(q);
    }
    
    console.debug('[getNestedComments] server snapshot', {
      parentCommentId,
      fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
      size: snapshot?.size,
      hasPendingWrites: snapshot?.metadata?.hasPendingWrites ?? 'unknown',
      fetchPolicy
    });
    
    const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { parentCommentId, comments, nextCursor, source: 'server' };
    
  } catch (err) {
    // Fallback without orderBy (e.g., when composite index isn't created yet)
    await ensureFreshToken();
    
    q = cursor
      ? query(commentsRef, where('parentCommentId', '==', parentCommentId), startAfter(cursor), limit(pageSize))
      : query(commentsRef, where('parentCommentId', '==', parentCommentId), limit(pageSize));
    
    // Try cache first in fallback as well
    if (fetchPolicy === 'cache-only' || fetchPolicy === 'cache-first') {
      try {
        const cachedSnap = await getDocsFromCache(q);
        if (!cachedSnap.empty || fetchPolicy === 'cache-only') {
          console.debug('[getNestedComments:fallback] cache snapshot', {
            parentCommentId,
            fromCache: cachedSnap?.metadata?.fromCache ?? 'unknown',
            size: cachedSnap?.size,
            hasPendingWrites: cachedSnap?.metadata?.hasPendingWrites ?? 'unknown'
          });
          const comments = cachedSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const nextCursor = cachedSnap.docs.length === pageSize ? cachedSnap.docs[cachedSnap.docs.length - 1] : null;
          return { parentCommentId, comments, nextCursor, source: 'cache' };
        }
      } catch (_) {}
      if (lowQuota) {
        return { parentCommentId, comments: [], nextCursor: null, source: 'skipped' };
      }
    }
    
    let snapshot;
    try {
      snapshot = await (typeof getDocsFromServer === 'function' ? getDocsFromServer(q) : getDocs(q));
    } catch (e2) {
      snapshot = await getDocs(q);
    }
    
    console.debug('[getNestedComments:fallback] server snapshot', {
      parentCommentId,
      fromCache: snapshot?.metadata?.fromCache ?? 'unknown',
      size: snapshot?.size,
      hasPendingWrites: snapshot?.metadata?.hasPendingWrites ?? 'unknown',
      fetchPolicy
    });
    
    const comments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const nextCursor = snapshot.docs.length === pageSize ? snapshot.docs[snapshot.docs.length - 1] : null;
    return { parentCommentId, comments, nextCursor, source: 'server' };
  }
}

// Add a nested comment (reply to a comment)
export async function addNestedComment({ reasonId, parentCommentId, userId, userName, userProfession, text }) {
  // Basic validation and auth checks to avoid silent rule failures
  if (!reasonId || !parentCommentId || !text) {
    const err = new Error('Invalid nested comment payload');
    console.error('[addNestedComment] Invalid payload', { reasonId, parentCommentId, hasText: !!text });
    throw err;
  }

  const authUid = auth?.currentUser?.uid;
  if (!authUid) {
    const err = new Error('Not authenticated');
    console.error('[addNestedComment] No authenticated Firebase user present');
    throw err;
  }
  
  // Always use the authenticated UID to satisfy security rules
  const uidToUse = authUid;
  if (userId && authUid !== userId) {
    console.warn('[addNestedComment] userId mismatch (overriding with auth.uid)', { providedUserId: userId, authUid });
  }

  const commentsRef = collection(db, 'solution_reason_comments');
  const payload = {
    reasonId,
    parentCommentId,
    userId: uidToUse,
    userName: userName ?? 'User',
    userProfession: userProfession ?? '',
    text,
    timestamp: serverTimestamp()
  };

  try {
    // Ensure the ID token is ready before Firestore write (helps right after login/refresh)
    await ensureFreshToken();
    
    console.debug('[addNestedComment] Writing nested comment', { 
      reasonId, 
      parentCommentId, 
      authUid, 
      hasText: !!text, 
      payloadKeys: Object.keys(payload) 
    });
    
    const docRef = await addDoc(commentsRef, payload);

    // Increment repliesCount on parent comment doc via Cloud Function (bypasses owner-only rule)
    try {
      const incrementReplies = httpsCallable(functions, 'incrementSolutionReasonReplyCount');
      await incrementReplies({ parentCommentId, delta: 1 });
    } catch (e) {
      console.warn('[addNestedComment] repliesCount increment via CF failed:', e?.code || e?.message || e);
    }

    // Also increment commentsCount on parent reason doc via Cloud Function
    try {
      const incrementComments = httpsCallable(functions, 'incrementSolutionReasonCommentCount');
      await incrementComments({ reasonId, delta: 1 });
    } catch (e) {
      console.warn('[addNestedComment] reason commentsCount increment via CF failed:', e?.code || e?.message || e);
    }
    
    return {
      reasonId,
      parentCommentId,
      comment: {
        id: docRef.id,
        reasonId,
        parentCommentId,
        userId: uidToUse,
        userName: payload.userName,
        userProfession: payload.userProfession,
        text,
        timestamp: new Date()
      }
    };
  } catch (error) {
    console.error('[addNestedComment] Firestore write failed', {
      code: error?.code,
      message: error?.message,
      payloadSummary: { 
        reasonId, 
        parentCommentId, 
        userId, 
        hasText: !!text, 
        payloadKeys: Object.keys(payload || {}) 
      },
    });
    throw error;
  }
}


